import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { useTheme, buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import useMobileLayout from 'Hooks/useMobileLayout';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import Button from 'Components/UI/Button';
import fr from 'facet-rest';
import PaymentDetails from 'Components/ComponentLibrary/Components/Uncategorized/PaymentDetails';
import {
  FDBodyText, FDLozenge, FDPrimaryActionButton, FDStack,
} from '@facet-wealth/design';
import { relationshipType } from 'facet-rest/src/consts/relationships';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { useCancelChurn } from 'Hooks/useCancelChurn';
import {
  root,
  section,
  title,
  links as linkContainer,
  link as linkButton,
  button,
  descriptionContainer,
  descriptionText,
  mobile,
  descriptionTextMobile,
  linkMobile, linkButtonWrapper,
} from '../sectionClasses';
import {
  buttonMobile,
} from '../../profileClasses';

const useStyles = buildUseStyles('MembershipAndBilling', (theme) => ({
  // Main Classes
  root: root(theme),
  section: section(theme),
  title: title(theme),
  descriptionContainer: descriptionContainer(theme),
  descriptionText: descriptionText(theme),
  linkContainer: linkContainer(theme),
  linkButtonWrapper: linkButtonWrapper(theme),
  linkButton: linkButton(theme),
  button: button(theme),

  // Mobile Classes
  mobile: mobile(theme),
  descriptionTextMobile: descriptionTextMobile(theme),
  linkMobile: linkMobile(theme),
  buttonMobile: {
    ...buttonMobile(theme),
    margin: 0,
  },
}));

const MembershipAndBilling = ({
  className,
  title: titleText,
  description,
  links,
  resource,
}) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const isMobile = useMobileLayout();
  const { loadPageContentLink } = usePageContentContext();
  const { loadModalCollectionByLink } = useModalContext();
  const { displayErrorMessage } = useSnackbarContext();
  const { onCancelChurn } = useCancelChurn(resource.relationship(fr.relationships.estatePlanningChurnCancel.name));

  const excludedLinks = [
    fr.relationships.accountDebitEnrollment.name, // This link is handled separately with a CTA button
    fr.relationships.estatePlanningChurnCancel.name, // This link has a different onclick handler
  ];

  const includedLinks = links.filter(link => !excludedLinks.includes(link.attr('name')));

  const handleOnClick = (relationship) => {
    // switch on meta type to open as hyperlink or as modal
    const type = relationship.meta('type');
    const link = relationship.link('related');
    const url = link.url();
    let err;
    switch (type) {
      case relationshipType.pagelink:
        loadPageContentLink(link);
        break;
      case relationshipType.modalLink:
        loadModalCollectionByLink(url);
        break;
      case relationshipType.hyperlink:
        window.open(url, '_blank');
        break;
      default:
        err = new Error('Unknown action for estate planning profile step action.');
        displayErrorMessage(err);
    }
  };

  return (
    <Box className={classNames(classes.root, className, { [classes.mobile]: isMobile })}>
      <Box className={classes.section}>
        <Typography
          className={classes.title}
          variant={'h6'}
          color={'black'}
        >
          {titleText}
        </Typography>
        <FDStack direction={'column'} spacing={1} alignItems={'flex-start'} paddingY={1}>
          <FDBodyText value={resource.attr('accountDebitEligibleText')} />
          {resource.withRelationship(
            fr.relationships.accountDebitEnrollment.name,
            (rel) => (
              <FDPrimaryActionButton
                icon={'arrowRight'}
                label={rel.meta('prompt')}
                onClick={() => loadPageContentLink(rel.link('related'))}
              />
            ),
            () => {},
          )}
        </FDStack>
      </Box>
      <Box
        className={classNames(classes.section, classes.descriptionContainer, { [classes.descriptionContainerMobile]: isMobile })}
      >
        <PaymentDetails
          imgSrc={resource.relationship(fr.relationships.logo.name)?.link('related').url()}
          details={resource.attr('paymentMethodInformation')}
          additionalInformation={resource.attr('lastPaymentInformation')}
          expired={resource.isTruthy('paymentMethodExpired')}
          expirationDateString={resource.attr('paymentMethodExpirationDate')}
        />
        {(!isMobile || description) && (
          <Typography
            className={classNames(classes.descriptionText, { [classes.descriptionTextMobile]: isMobile })}
            variant={'h6'}
          >
            {description}
          </Typography>
        )}
      </Box>
      <Box className={classNames(classes.section, classes.linkContainer)}>
        {includedLinks.map(link => (
          <div className={classNames(className, classes.linkButtonWrapper)}>
            <LinkButton relationship={link} onClick={() => handleOnClick(link)} classes={classes} />
          </div>
        ))}
        {resource.withRelationship(
          fr.relationships.estatePlanningChurnCancel.name,
          (rel) => (
            <div className={classNames(className, classes.linkButtonWrapper)}>
              <LinkButton relationship={rel} onClick={onCancelChurn} classes={classes}>
                {rel.meta('alertMessage') ? (
                  <FDStack paddingX={1} justifyContent={'center'}>
                    <FDLozenge label={rel.meta('alertMessage')} color={'error'} />
                  </FDStack>
                ) : null}
              </LinkButton>
            </div>
          ),
          () => {},
        )}
      </Box>
    </Box>
  );
};

const LinkButton = ({
  relationship,
  onClick,
  classes,
  children,
}) => {
  const isMobile = useMobileLayout();

  return (
    <Button
      className={classNames(classes.linkButton, { [classes.linkMobile]: isMobile })}
      color={'primary'}
      key={`${relationship.attr('name')}-link`}
      onClick={onClick}
      test={`profile-page-section-${relationship.attr('name')}`}
    >
      {relationship.meta('prompt')}
      {children}
    </Button>
  );
};

LinkButton.defaultProps = {
  children: null,
};

LinkButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  relationship: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  children: PropTypes.node,
};


MembershipAndBilling.defaultProps = {
  className: '',
  title: '',
  description: '',
  links: [],
};

MembershipAndBilling.propTypes = {
  className: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  links: PropTypes.arrayOf(PropTypes.instanceOf(fr.Link)),
  resource: PropTypes.instanceOf(fr.Resource).isRequired,
};

export default MembershipAndBilling;
